import { z } from "zod";
import { Id, IsoDateTime } from "./common";

/**
 * MVP feature key set per
 * designs/asset_monitoring_mvp_domain_data_model.md §4.3.
 *
 * Adding a feature here is a contract change: backend `requireFeature`,
 * the role->permission map, and the Admin UI feature toggle list must
 * all be updated together.
 *
 * Airport site intelligence additions (Phase 0):
 *   TRAFFIC_LAYER       — enables road-traffic overlay on map (Google TrafficLayer)
 *   PLACES_SITE_SETUP   — enables Places-assisted address/location search for site setup
 *   ROUTES_ETA          — enables landside travel-time ETA (Google Routes API)
 *   SITE_MODEL_3D       — reserved for future photorealistic 3D tile visualization
 */
export const FeatureKey = z.enum([
  "MAP_VIEW",
  "WEATHER_LAYER",
  "TRAFFIC_LAYER",
  "PLACES_SITE_SETUP",
  "ROUTES_ETA",
  "SITE_MODEL_3D",
  "WORK_BOARD",
  "OUTBOUND_COMMANDS",
  "INTEGRATION_HEALTH",
  "AUDIT_LOG_VIEW",
  "USER_MANAGEMENT",
  "PLATFORM_ADMIN",
  "SHEETS_EXPORT",
  "ADVANCED_RISK",
]);
export type FeatureKey = z.infer<typeof FeatureKey>;

export const FEATURE_KEYS: readonly FeatureKey[] = FeatureKey.options;

export const FeatureScope = z.enum(["Tenant", "Role", "User", "Environment"]);
export type FeatureScope = z.infer<typeof FeatureScope>;

export const FeatureEnvironment = z.enum(["Dev", "Staging", "Production"]);
export type FeatureEnvironment = z.infer<typeof FeatureEnvironment>;

export const FeatureEntitlement = z.object({
  featureEntitlementId: Id,
  tenantId: Id,
  featureKey: FeatureKey,
  isEnabled: z.boolean(),
  scope: FeatureScope,
  roleId: Id.nullable(),
  userId: Id.nullable(),
  environment: FeatureEnvironment.nullable(),
  effectiveFrom: IsoDateTime,
  effectiveTo: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  createdBy: Id.nullable(),
  updatedBy: Id.nullable(),
});
export type FeatureEntitlement = z.infer<typeof FeatureEntitlement>;

export const FeatureEntitlementUpsertInput = z.object({
  featureKey: FeatureKey,
  isEnabled: z.boolean(),
  scope: FeatureScope.default("Tenant"),
  roleId: Id.nullable().optional(),
  userId: Id.nullable().optional(),
  environment: FeatureEnvironment.nullable().optional(),
  effectiveFrom: IsoDateTime.optional(),
  effectiveTo: IsoDateTime.nullable().optional(),
});
export type FeatureEntitlementUpsertInput = z.infer<
  typeof FeatureEntitlementUpsertInput
>;
